import type { ChatPlan } from "@repo/shared/types/chat-plan";
import { CodeBlock } from "@repo/ui/ai-elements/code-block";
import { MessageResponse } from "@repo/ui/ai-elements/message";
import { Button } from "@repo/ui/button";
import { Icon } from "@repo/ui/components/icon";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@repo/ui/ui/collapsible";

/**
 * Renders a structured plan proposed by the supervisor in Plan mode. The
 * "Approve & build" action is only shown on the latest plan in the transcript
 * (`showApprove`); earlier plans stay readable but inert. Approving flips the
 * chat back to Auto mode and sends an execution message — see
 * {@link ../chat.tsx}'s `handleApprovePlan`.
 */
export const PlanCard = ({
  plan,
  showApprove,
  disabled,
  onApprove,
}: {
  plan: ChatPlan;
  showApprove: boolean;
  disabled: boolean;
  onApprove: () => void;
}) => (
  <div className="my-1 flex flex-col gap-3 rounded-lg border bg-card p-3 text-xs">
    <div className="flex items-start gap-2">
      <Icon name="clipboard" className="mt-0.5 size-4 shrink-0" />
      <div className="flex flex-col gap-0.5">
        <span className="font-medium text-sm">{plan.title}</span>
        <span className="text-muted-foreground">{plan.description}</span>
      </div>
    </div>

    {plan.datasets.length > 0 && (
      <PlanSection title="Datasets">
        {plan.datasets.map((ds, i) => (
          <div
            key={i}
            className="flex flex-col gap-1 rounded-md bg-muted/40 p-2"
          >
            <div className="flex items-center gap-1.5">
              <Icon name="dataset" className="size-3.5 shrink-0" />
              <span className="font-medium">{ds.title}</span>
              <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                {ds.targetTable}
              </span>
            </div>
            <span className="text-muted-foreground">{ds.prompt}</span>
            {ds.sqlSketch && (
              <Collapsible>
                <CollapsibleTrigger className="flex items-center gap-1 text-muted-foreground hover:text-foreground">
                  <Icon name="chevron-right" className="size-3" />
                  SQL sketch
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-1">
                  <CodeBlock code={ds.sqlSketch} language="sql" />
                </CollapsibleContent>
              </Collapsible>
            )}
          </div>
        ))}
      </PlanSection>
    )}

    {plan.blocks && plan.blocks.length > 0 && (
      <PlanSection title="Blocks">
        {plan.blocks.map((block, i) => (
          <div
            key={i}
            className="flex flex-col gap-0.5 rounded-md bg-muted/40 p-2"
          >
            <div className="flex items-center gap-1.5">
              <Icon name="blocks-plus" className="size-3.5 shrink-0" />
              <span className="font-medium">{block.title}</span>
              {block.displayType && (
                <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                  {block.displayType}
                </span>
              )}
            </div>
            <span className="text-muted-foreground">{block.description}</span>
          </div>
        ))}
      </PlanSection>
    )}

    {plan.boardFilters && plan.boardFilters.length > 0 && (
      <PlanSection title="Board filters">
        <ul className="flex flex-col gap-1">
          {plan.boardFilters.map((filter, i) => (
            <li
              key={i}
              className="flex items-center gap-1.5 text-muted-foreground"
            >
              <Icon name="filter" className="size-3.5 shrink-0" />
              {filter}
            </li>
          ))}
        </ul>
      </PlanSection>
    )}

    {plan.notes && (
      <PlanSection title="Notes">
        <MessageResponse compact>{plan.notes}</MessageResponse>
      </PlanSection>
    )}

    {showApprove && (
      <div className="flex items-center gap-2 pt-1">
        <Button size="sm" disabled={disabled} onClick={onApprove}>
          <Icon name="check" className="size-3.5" />
          Approve &amp; build
        </Button>
      </div>
    )}
  </div>
);

const PlanSection = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-1.5">
    <span className="font-medium text-[10px] text-muted-foreground uppercase tracking-wide">
      {title}
    </span>
    {children}
  </div>
);
